import { useEffect, useRef } from 'react'
import { serverEventSchema } from '@kontor/shared'
import { useQueryClient } from '@tanstack/react-query'

import { eventsSocketUrl } from '@/lib/api'
import { useConnectionStore } from '@/store/connections'
import { useMetricStore } from '@/store/metrics'
import { useTransferStore } from '@/store/transfers'

/** Backoff for reopening the live channel; the last value repeats. */
const RECONNECT_DELAYS_MS = [500, 1_000, 2_000, 5_000, 10_000]

/**
 * Keeps the websocket to `/api/events` open while the vault is unlocked. Connection status
 * arrives here rather than by polling, which is what §5.1 asks for; server list changes only
 * invalidate the query so the list refetches once.
 */
export function useServerEvents(enabled: boolean): void {
  const queryClient = useQueryClient()
  const attemptRef = useRef(0)
  const listingRefreshRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    if (!enabled) {
      useConnectionStore.getState().clear()
      useTransferStore.getState().clear()
      useMetricStore.getState().clear()
      return
    }

    let socket: WebSocket | undefined
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined
    let closedByEffect = false

    const open = (): void => {
      socket = new WebSocket(eventsSocketUrl())

      socket.onopen = () => {
        attemptRef.current = 0
        useConnectionStore.getState().setLive(true)
        // Whatever happened while the channel was down was missed, so take a fresh snapshot.
        void queryClient.invalidateQueries({ queryKey: ['transfers'] })
        void queryClient.invalidateQueries({ queryKey: ['connections'] })
      }

      socket.onmessage = (message: MessageEvent<string>) => {
        const parsed = serverEventSchema.safeParse(safeParse(message.data))
        if (!parsed.success) {
          // An event we do not understand is a version mismatch, not a reason to tear down the
          // channel — the rest of the stream stays useful.
          console.warn('Unbekanntes Ereignis vom Backend verworfen', parsed.error.issues)
          return
        }

        const event = parsed.data
        switch (event.type) {
          case 'connection.state':
            useConnectionStore.getState().apply(event.state)
            break
          case 'transfer.changed':
            useTransferStore.getState().apply(event.item)
            if (event.item.status === 'done') {
              // A finished file changed a directory somewhere, so the open panes are stale. The
              // refresh waits for the queue to go quiet — 200 files must not mean 200 refetches.
              clearTimeout(listingRefreshRef.current)
              listingRefreshRef.current = setTimeout(() => {
                void queryClient.invalidateQueries({ queryKey: ['files'] })
              }, 400)
            }
            break
          case 'metrics.sample':
            useMetricStore.getState().apply(event.sample)
            break
          case 'alert.changed':
            // The list is small and rarely changes; refetching beats mirroring it in a store.
            void queryClient.invalidateQueries({ queryKey: ['alert-events'] })
            break
          case 'server.changed':
            void queryClient.invalidateQueries({ queryKey: ['servers'] })
            break
          case 'vault.locked':
            useConnectionStore.getState().clear()
            useTransferStore.getState().clear()
            useMetricStore.getState().clear()
            void queryClient.invalidateQueries({ queryKey: ['vault-status'] })
            break
          case 'hello':
            break
        }
      }

      socket.onclose = () => {
        useConnectionStore.getState().setLive(false)
        if (closedByEffect) return

        const delay =
          RECONNECT_DELAYS_MS[Math.min(attemptRef.current, RECONNECT_DELAYS_MS.length - 1)] ??
          10_000
        attemptRef.current += 1
        reconnectTimer = setTimeout(open, delay)
      }

      socket.onerror = () => {
        // `close` follows and carries the reconnect logic; handling it twice would double the
        // backoff steps.
        socket?.close()
      }
    }

    open()

    return () => {
      closedByEffect = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      clearTimeout(listingRefreshRef.current)
      socket?.close()
      useConnectionStore.getState().setLive(false)
    }
  }, [enabled, queryClient])
}

function safeParse(raw: string): unknown {
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}
